import { generatePath, matchPath, PathMatch } from 'react-router';

type RouteParamType = string | number | boolean | undefined;

type RouteQueryParamOptions = Record<string, RouteParamType | RouteParamType[]>;

interface RouteLinkFunction<TRouteParams extends RouteParamType[]> {
  (...args: TRouteParams): string;

  (...args: [...TRouteParams, RouteQueryParamOptions]): string;
}

type RouteMatchFunction = (path: string, opts?: { end?: boolean; caseSensitive?: boolean }) => PathMatch<string> | null;

type RouteTestFunction<ORouteParams> = (
  path: string,
  params?: ORouteParams,
  opts?: { end?: boolean; caseSensitive?: boolean },
) => boolean;

type RouteType<TRouteParams extends RouteParamType[] = [], ORouteParams = object> = {
  pattern: string;
  linkPattern: string;
  relativePath: string;
  getLink: RouteLinkFunction<TRouteParams>;
  match: RouteMatchFunction;
  test: RouteTestFunction<ORouteParams>;
  addChildPattern: () => void;
};

function toSearchParams(obj: RouteQueryParamOptions): URLSearchParams {
  const params = new URLSearchParams();
  const append = (key: string, value: RouteParamType | RouteParamType[]): void => {
    if (Array.isArray(value)) {
      value.forEach((v) => append(key, v));
    } else if (typeof value === 'boolean') {
      params.append(key, value ? 'true' : 'false');
    } else if (value != null) {
      params.append(key, `${value}`);
    }
  };
  Object.entries(obj).forEach(([key, value]) => append(key, value));
  return params;
}

function makeTestFunction<ORouteParams>(pattern: string): RouteTestFunction<ORouteParams> {
  return (path, params, opts) => {
    const matched = matchPath({ path: pattern, ...opts }, path);
    if (!matched) {
      return false;
    }
    if (params) {
      for (const [k, v] of Object.entries(matched.params)) {
        if (params[k as keyof ORouteParams] !== v) {
          return false;
        }
      }
    }
    return true;
  };
}

export function makeRoute<
  TRouteParams extends RouteParamType[],
  PRouteParams extends RouteParamType[],
  ORouteParams extends Record<string, RouteParamType>,
  PORouteParams extends Record<string, RouteParamType>,
>(
  parent: RouteType<PRouteParams, PORouteParams> | undefined,
  pattern: string,
  getParams?: (...args: TRouteParams) => ORouteParams,
): RouteType<TRouteParams, ORouteParams> {
  if (parent) {
    parent.addChildPattern();
  }

  const relativePattern = pattern.replace(/^\//, '') || '/';
  const joined =
    parent === undefined ? pattern : `${parent.linkPattern.replace(/\/$/, '')}/${pattern.replace(/^\//, '')}`;
  const encoded = joined
    .split('/')
    .map((part) => encodeURIComponent(part).replace(/^%3A/, ':').replace(/%3F$/, '?'))
    .join('/');

  let hasChildren = false;

  const getPattern = () => (hasChildren ? `${joined}/*` : joined);

  const route: RouteType<TRouteParams, ORouteParams> = {
    get pattern() {
      return getPattern();
    },

    linkPattern: joined,

    get relativePath() {
      return hasChildren ? `${relativePattern}/*` : relativePattern;
    },

    getLink: (...args: unknown[]) => {
      const last = args[args.length - 1] as RouteQueryParamOptions;
      const routeParams = (typeof last === 'object' ? args.slice(0, args.length - 1) : args) as TRouteParams;
      const params = getParams?.(...routeParams);
      const stringParams: Record<string, string> = {};
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value != null) {
            stringParams[key] = String(value);
          }
        }
      }
      let path = generatePath(encoded, stringParams);

      if (typeof last === 'object') {
        const qParams = toSearchParams(last).toString();
        if (qParams.length > 0) {
          path += `?${qParams}`;
        }
      }

      return path;
    },
    match: (path, opts) => matchPath({ path: joined, ...opts }, path) ?? null,
    test: makeTestFunction(joined),
    addChildPattern: () => {
      hasChildren = true;
    },
  };
  return route;
}

const marketLocationIdParam = (marketLocationId: string) => ({ marketLocationId }) as const;

export const root = makeRoute(undefined, '/');
export const imprint = makeRoute(root, '/imprint');
export const privacy = makeRoute(root, '/privacy');
export const termsOfService = makeRoute(root, '/terms-of-service');
export const infoCenter = makeRoute(
  root,
  '/info-center/:path?/:subpath?',
  (path?: string, subpath?: string) =>
    ({
      path,
      subpath,
    }) as const,
);

export const optimizationDownload = makeRoute(root, '/optimization');
export const salesPartnerSettings = makeRoute(root, '/sales-partner');
export const userManagement = makeRoute(root, '/user-management');

export const loginPage = makeRoute(root, '/login');
export const authCallback = makeRoute(loginPage, '/callback');

export const oldMarketLocation = makeRoute(root, '/markt-lokationen/:marketLocationId', marketLocationIdParam); // Still needed during the transition period
export const marketLocation = makeRoute(root, '/market-location/:marketLocationId', marketLocationIdParam);
export const optimizationGroups = makeRoute(marketLocation, '/optimization-groups', marketLocationIdParam);
export const optimization = makeRoute(marketLocation, '/optimization', marketLocationIdParam);
export const marketLocationFeedIn = makeRoute(marketLocation, '/feed-in', marketLocationIdParam);
export const marketLocationForecast = makeRoute(marketLocation, '/forecast', marketLocationIdParam);
export const marketLocationOutages = makeRoute(marketLocation, '/outages', marketLocationIdParam);
export const marketLocationAnnualPlanning = makeRoute(marketLocation, '/annual-planning', marketLocationIdParam);
export const marketLocationMasterData = makeRoute(marketLocation, '/master-data', marketLocationIdParam);
export const marketLocationRevenueReport = makeRoute(marketLocation, '/revenue-report', marketLocationIdParam);
export const marketLocationRevenueQuarterlyReport = makeRoute(
  marketLocationRevenueReport,
  '/quarterly-report',
  marketLocationIdParam,
);

export const marketLocationMasterDataMachines = makeRoute(marketLocationMasterData, '/machines', marketLocationIdParam);
export const marketLocationMasterDataDevices = makeRoute(marketLocationMasterData, '/devices', marketLocationIdParam);
